import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { useFetch } from '@/utils/fetch';

import { ArticleDefectWithRelations } from './request';

export const isCustom = (articleDefect: { defectTypeOrganizationId: null | string }) =>
  !articleDefect.defectTypeOrganizationId;

export type DefectTypeFromGetMany = Endpoints['GET /defect-types']['response'][number];
export const useDefectTypes = (
  params: Endpoints['GET /defect-types']['query'],
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /defect-types']>();

  return useQuery({
    queryKey: ['defect-types', params],
    queryFn: () => fetch('/defect-types', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useDefectTypesOrganization = () => {
  const fetch = useFetch<Endpoints['GET /defect-types/organization']>();

  return useQuery({
    queryKey: ['defect-types', 'organization'],
    queryFn: () => fetch('defect-types/organization'),
  });
};

export const useDeleteDefectType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /defect-types/:id']>();

  return useMutation({
    mutationFn: (id: string) =>
      fetch(`defect-types/${id}`, undefined, {
        method: 'DELETE',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['defect-types'] });
    },
  });
};

export const useUpdateDefectType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /defect-types/:id']>();

  return useMutation({
    mutationFn: ({
      defectTypeId,
      ...body
    }: Endpoints['PATCH /defect-types/:id']['body'] & { defectTypeId: string }) =>
      fetch(`/defect-types/${defectTypeId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['defect-types', 'organization'] });
    },
  });
};

export const useCreateDefectType = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /defect-types']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /defect-types']['body']) =>
      fetch(`/defect-types`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['defect-types', 'organization'] });
    },
  });
};

export const useGetDefectName = () => {
  const { _db } = useDatabaseTranslation();

  return (defect: {
    defectTypeOrganization: { name: I18nWording | null } | null;
    customDescription: string | null;
  }) => {
    if (defect.defectTypeOrganization?.name) {
      return _db(defect.defectTypeOrganization.name);
    } else {
      return defect.customDescription ?? '';
    }
  };
};

export const useDefectName = (defect: ArticleDefectWithRelations) => {
  const getDefectName = useGetDefectName();

  return getDefectName(defect);
};

/**
 *
 * @returns Returns true if both defects are of the same type
 */
const areDefectsDuplicates = (
  defect1: ArticleDefectWithRelations,
  defect2: ArticleDefectWithRelations
): boolean => {
  return (
    (isCustom(defect1) &&
      isCustom(defect2) &&
      defect1.customDescription === defect2.customDescription) ||
    (!!defect1.defectTypeOrganizationId &&
      defect1.defectTypeOrganizationId === defect2.defectTypeOrganizationId)
  );
};

/**
 *
 * @param defectToCompare The defect we want to attribute a number to (only if it has duplicate defects)
 * @param defects The list of selected defects
 * @returns A number attributed to a defect (only if it has duplicate defects)
 */
export const computeDuplicateDefectNumber = (
  defectToCompare: ArticleDefectWithRelations,
  defects: ArticleDefectWithRelations[]
): number | undefined => {
  const currentActionAndDuplicates = defects.filter((defect) =>
    areDefectsDuplicates(defectToCompare, defect)
  );

  if (currentActionAndDuplicates.length === 1) {
    return undefined;
  }

  const indexOfActionToCompare = currentActionAndDuplicates.findIndex(
    (defect) => defect.id === defectToCompare.id
  );

  return indexOfActionToCompare + 1;
};

/**
 * Formats a list of defects so that defects of the same type are merged into a single defect for which:
 * - `quantity` is total of merged defects
 * - `ids` is a list of the merged defects' ids
 * - the other fields simply correspond to the first of the merged defects
 *
 * Defects are of the same type if they have the same `customDescription` or `defectTypeOrganizationId`
 */
export const getGroupedDefects = (defects: ArticleDefectWithRelations[]) => {
  return defects.reduce<(ArticleDefectWithRelations & { ids: string[]; quantity: number })[]>(
    (acc, defect) => {
      const existingDefect = acc.find((accDefect) => areDefectsDuplicates(defect, accDefect));

      if (!existingDefect) {
        acc.push({
          ...defect,
          ids: [defect.id],
          quantity: 1,
        });
      } else {
        existingDefect.ids = [...existingDefect.ids, defect.id];
        existingDefect.quantity = existingDefect.quantity + 1;
      }

      return acc;
    },
    []
  );
};
